(ns leg.article
  "The article itself.

  The body is put into the page as markup, which is the one thing a web
  page must not do with a document from a stranger - so it is not done with
  one. What arrives here has been through `leser.html` on the server:
  parsed, walked against an allow-list and written out again, with no
  script, no style, no `javascript:` and no `data:` left in it. That is why
  `dangerouslySetInnerHTML` is defensible here and nowhere else in this
  window, and why the sanitising is on the server rather than beside this
  file - one implementation, on the side that cannot be skipped.

  Every link in it opens in a new window, and so does the title: what is
  being read here is somebody else's page, and following a link should not
  cost the reader the list they were working through. The server writes the
  `target` and the `rel`, again because it is the one place that sees every
  link.

  In the installed app a new window is not far enough - it is a window
  inside Leser, with no address bar and the list gone behind it. So every
  link out of here, the ones in the body as much as the headline, is handed
  to the phone instead where that can be said at all. Nothing in this file
  does that: it is one listener on the document
  (`leg.pwa/install-links!`), because the body is markup and has no
  components to put it on.

  The headline is the only way out, and it is the whole of it. There was a
  button in the bar that did the same thing, and a reader has one thing to
  open: the article it is showing. The title of the page being read is where
  anybody looks for the page itself, and a word in the bar that duplicated
  it was a press competing with the three that only this bar can do.

  The list is still there while one of its articles is being read, so it can
  be walked without coming back out to it: a finger dragged sideways, the
  arrow keys or `h` and `l` (`leg.keys`), or the two arrows at the foot of
  the column. All three stop at the ends rather than wrapping - see
  `leg.state/step-article!`, and all three turn the page the same way, which
  is the way the finger does it: the page is pushed aside and the next one
  comes in behind it (`leg.vt`). The bar does not go with it - see
  `ArticlePane`."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [kc.core :as kc]
            [leg.mui :as mui :refer [sx]]
            [leg.nav :as nav]
            [leg.scroll :as scroll]
            [leg.state :as state]
            [leg.swipe :as swipe]
            [leg.theme :as theme]
            [leg.util :as u]))

(def-ui Body [html]
  ($ :div {:class "leser-body"
           :dangerouslySetInnerHTML #js {:__html (or html "")}}))

(def-ui Enclosure
  "What came attached - a podcast episode, usually. Offered as a link
  rather than played here: a reader is not a media player, and the
  browser's own is one click away."
  [enclosure]
  (when-let [url (:url enclosure)]
    ($ mui/Box {:sx (sx {:marginTop "1rem"})}
       ($ :a {:href url :target "_blank" :rel "noopener noreferrer"}
          (str "Attachment" (when-let [t (:type enclosure)] (str " (" t ")")))))))

(def-ui Sheet
  "The article as it is set: which paper it came out of, the headline, who
  wrote it and when, and the prose.

  A component of its own because on a phone it is drawn three times - the
  page being read and the two stuck to its edges (`Peek`) - and the page
  the finger pulls into view has to be the same page it turns into, down to
  the measure. Two of them that drifted apart would show it as a jump at
  the one moment the whole swipe exists to make smooth."
  [article feed]
  (let [{:article/keys [title url author published first-seen body teaser enclosure]} article]
    ($ mui/Box {:sx (sx {:maxWidth "42rem" :margin "0 auto"
                         :padding "1.5rem 1.25rem 4rem"})}
       ;; The kicker: which paper this came out of, over the
       ;; headline and in the one colour this page has.
       (when-let [kicker (or (:feed/title feed) (u/host url))]
         ($ :div {:style {:fontFamily theme/font-ui :fontSize ".64rem"
                          :fontWeight 600 :textTransform "uppercase"
                          :letterSpacing ".14em" :marginBottom ".3rem"}}
            ($ mui/Box {:component "span" :sx (sx {:color "primary.main"})}
               kicker)))
       ;; The one heading on the screen. `h5` is the size a paper
       ;; would set it at and `h1` is what it is: there is nothing
       ;; above the headline of the thing being read.
       ($ mui/Typography
          {:variant "h5" :component "h1"
           :sx (sx {:lineHeight 1.14 :marginBottom ".45rem"})}
          (if url
            ($ :a {:href url :target "_blank" :rel "noopener noreferrer"
                   :style {:color "inherit" :textDecoration "none"}}
               title)
            title))
       ;; Who wrote it and when, under a rule, which is where a
       ;; paper puts it and what separates the headline from the
       ;; text without a gap the size of a hand.
       ($ mui/Typography
          {:sx (sx {:color "text.secondary" :fontFamily theme/font-ui
                    :fontSize ".66rem" :textTransform "uppercase"
                    :letterSpacing ".08em" :paddingBottom ".6rem"
                    :borderBottom "1px solid" :borderColor "divider"
                    :marginBottom "1rem"})}
          (->> [author (or (u/stamp published) (u/stamp first-seen))]
               (remove nil?)
               (interpose " \u00b7 ")
               (apply str)))
       (if body
         ($ Body {:html body})
         ($ mui/Typography {:sx (sx {:color "text.secondary"})}
            (or teaser "This article has no text in the feed. Open it to read it.")))
       ($ Enclosure {:enclosure enclosure}))))

(def-ui Step
  "One step along the list. An arrow rather than a word: it moves in the
  order of the list, which has no name, and it has to fit beside the three
  buttons that do."
  [label title on-click enabled?]
  ($ mui/Tooltip {:title title}
     ;; The span is there so the tooltip still has something to hang on
     ;; when the button is disabled at the end of the list.
     ($ :span {}
        ($ mui/Button {:disabled (not enabled?)
                       :onClick (fn [_] (on-click))
                       :aria-label title
                       ;; A shape and not a word: the bar sets the size of
                       ;; these (`leg.nav/Bar`, `data-glyph`).
                       :data-glyph true
                       :sx (sx {:minWidth "2rem" :paddingLeft ".25rem"
                                :paddingRight ".25rem" :lineHeight 1})}
           label))))

(def-ui Mark
  "One of the two marks an article carries, drawn as the mark itself:
  filled where it is set, hollow where it is not.

  A glyph and not a word, because what somebody wants off this button at a
  glance is which way it is now - and the words could not say that. `Star`
  and `Starred` were a press and a state in the same place, `Read` and
  `Unread` two presses, and a bar that said `Starred` beside `Unread` said
  one thing about the article and one about what pressing would do to it.
  A filled star is a starred article in every reader there has ever been.

  The word is still there for anybody who cannot see the shape: the tooltip
  and the `aria-label` say what the press does, and `aria-pressed` says
  which way it is now. The accent is the same one the filters use for the
  option that is on (`leg.nav/Filters`)."
  [on? on-glyph off-glyph title on-click]
  ($ mui/Tooltip {:title title}
     ($ mui/Button {:onClick (fn [_] (on-click))
                    :aria-label title
                    :aria-pressed (boolean on?)
                    :color (if on? "primary" "inherit")
                    :data-glyph true
                    :sx (sx (merge {:minWidth "2rem" :paddingLeft ".25rem"
                                    :paddingRight ".25rem" :lineHeight 1}
                                   (when-not on? {:color "text.secondary"})))}
        (if on? on-glyph off-glyph))))

(def-ui Count
  "How far along the list this article is: `17 / 34`, between the two
  arrows that move it.

  It belongs between them because it is what they are moving through, and
  because a list of thirty-four is a list somebody can decide to finish -
  two arrows with nothing between them say which way, never how much is
  left.

  The window's list and not the server's (`leg.state/position`): the number
  on the right is the length of the list these arrows can reach, filtered
  and cut, and not the feed's whole count. Figures of the same width
  (`tabular-nums`), or the arrows either side would shift a pixel at every
  turn of the page - this is the one label in the bar that changes while
  nothing else does."
  [at of]
  ($ mui/Box {:component "span"
              :aria-label (str "Article " at " of " of)
              :sx (sx {:fontFamily theme/font-ui :fontSize ".72rem"
                       :color "text.secondary" :whiteSpace "nowrap"
                       :fontVariantNumeric "tabular-nums"})}
     (str at " / " of)))

(def-ui Toolbar
  "What can be done with the article that is open, in the bar every screen in
  this window has at its foot (`leg.nav`) - this is the row the others were
  made to look like.

  [Home], then the list: where it is being walked and how far along. What is
  done to the article rather than to the window is on the right, under the
  thumb that is already there.

  Three presses and no fourth. [Back] was here and is gone: the back button
  does it, the hardware one on a phone and the browser's on a machine
  (`leg.route` put the article in the address so that it would), and a word
  in the bar for the one move every screen already has was a press that
  taught somebody the wrong way out. [Open] is gone the same way - the
  headline is the link (see the namespace above)."
  [article narrow? previous? next? position]
  (let [{:article/keys [id read starred]} article]
    ($ nav/Bar
       {:label "Article"
        ;; The two steps with the count between them: far enough apart that
        ;; neither is the wrong half of the other - walking the list is what
        ;; this bar is pressed for, over and over - and what they are
        ;; walking through is the thing in the middle.
        :gap ".9rem"
        ;; Only on a narrow screen: there the article is the whole of it and
        ;; this is the only bar there is. On a wide one the sidebar's bar is
        ;; two columns to the left with [Home] in it.
        :left (when narrow? ($ nav/Home))
        ;; In the middle of the bottom edge, which is where a thumb already
        ;; is.
        :middle ($ :<>
                   ($ Step {:label "\u2039" :title "Previous article"
                            :on-click state/previous-article! :enabled? previous?})
                   (when position
                     ($ Count {:at (first position) :of (second position)}))
                   ($ Step {:label "\u203a" :title "Next article"
                            :on-click state/next-article! :enabled? next?}))
        :right ($ :<>
                  ($ Mark {:on? starred
                           :on-glyph "\u2605" :off-glyph "\u2606"
                           :title (if starred "Remove star" "Star this article")
                           :on-click #(state/set-starred! id (not starred))})
                  ($ Mark {:on? read
                           :on-glyph "\u25cf" :off-glyph "\u25cb"
                           :title (if read "Mark unread" "Mark read")
                           :on-click #(state/set-read! id (not read))}))})))

(def-ui Peek
  "The page next door, stuck to the edge of the one being read.

  A page turn on a phone is two opaque pages edge to edge, one leaving as
  the other arrives - and the first half of that movement is the finger's.
  So there has to be something at the edge for the drag to pull into view,
  or what opens up under the thumb is the paper behind the window: a
  cream-coloured strip, widening, with nothing in it. These are the two
  articles the look-ahead already fetched (`leg.state/use-neighbours`),
  drawn one screen out on either side *inside* the column the finger moves.
  Children of it, so they travel with it for nothing: one transform moves
  all three.

  It is a page and not a placeholder - the same `Sheet`, set to the same
  measure - because a moment later it is replaced by the real one at
  exactly the offset it had reached: the picture the browser takes of the
  column has the peek in it, and the page coming in is drawn from the same
  edge the peek had reached. Anything that differed would show up as a
  flinch at the hand-over, which is the one moment the whole thing exists
  to smooth over.

  No bar at its foot, because the bar is not part of the page any more: it
  stands still while the pages move past it (`ArticlePane`), so a peek with
  one would be a second bar sliding in beside the one that stayed.

  Not scrolled, not reachable, not announced: `pointer-events: none`
  because the finger on it is the finger on the page beside it, `inert`
  because a headline off the side of the screen is not somebody's next tab
  stop, `aria-hidden` because it is one page said twice to a screen reader
  that has no edges to look over.

  Only where the page is the whole screen. On a wide one the article is a
  column with two others beside it, and a page a screen to the left would
  be drawn over the list - which is the same reason the drag itself is
  refused there (`leg.vt/one-pane?`)."
  [article feed side]
  (when article
    ($ mui/Box {:className (str "leser-paper leser-peek leser-peek-" (name side))
                :aria-hidden true :inert true}
       ;; The same box the real page scrolls in, down to the `auto`: a
       ;; scrolling box reserves room for a scrollbar on a machine that
       ;; draws one, and a peek that did not would set its prose a few
       ;; pixels wider than the page it is about to become.
       ($ mui/Box {:sx (sx {:flex "1" :overflowY "auto"})}
          ($ Sheet {:article article :feed feed})))))

(def-ui ArticlePane
  "The article, and the `main` of this window: on a wide screen it is the
  thing the other two columns are for, and on a narrow one it is the whole
  screen (see `leg.articles/ArticlesPane` for the other half of that).

  What is being fetched is said with a bar at the head of the column rather
  than by emptying it: the article that is open stays until the next one is
  there, so stepping through a feed is one page replacing another and not a
  blink of nothing in between (`leg.vt`).

  Two boxes and not one, and which is which is the whole of how the swipe
  looks. The page is the inner one - the prose, and on a phone the two
  pages off either edge of it (`Peek`) - and it is the only thing that
  moves: the finger drags it, the slide carries it on, the view transition
  captures it (`.leser-vt-read`). The bar is outside it and stands still.
  It used to be inside, so a swipe took it along and the controls left the
  screen with the article; but the bar is not part of what is being read.
  It is the frame the reading happens in, the next page's arrows are the
  same arrows, and a row of buttons sliding off to the left was movement
  that said something had changed about them when nothing had."
  [narrow?]
  (let [st (state/use-state)
        {:keys [article feeds article-loading?]} st
        id (:article/id article)
        {before :previous after :next} (state/use-neighbours)
        feed-of (fn [a] (first (filter #(= (:article/feed a) (:feed/id %)) feeds)))
        ;; One gesture, in four parts: where the finger is, which way it
        ;; went, and what to do when it went nowhere.
        gesture {:track  state/drag-article!
                 :left   state/next-article!
                 :right  state/previous-article!
                 :settle state/settle-article!}]
    ;; Where this article was last put down, which for one that has never
    ;; been opened is its first line (`leg.scroll`). Every article is drawn
    ;; into the same box and a scroll position belongs to the box, so
    ;; without this the next article opens two screens down, wherever the
    ;; last one was left.
    ;;
    ;; A layout effect, for the one reason that matters here: the article
    ;; is swapped inside a view transition's callback (`leg.vt`,
    ;; `react-dom/flushSync`), and React runs layout effects before that
    ;; call returns - so this lands between the new text arriving and the
    ;; browser's first picture of it. An ordinary effect would be a second
    ;; move after the slide was over, and a scroll before the swap would be
    ;; cut short by whatever the page being left was long.
    (kc/use-layout-effect
     [id]
     (when id (scroll/to-mark! (state/read-box) id)))
    ($ mui/Box {:component "main"
                :className "leser-paper"
                :aria-busy (boolean article-loading?)
                ;; `minHeight: 0` because this box is a column of the window
                ;; (`leg.app/Wide`) and the article in it is long: without it
                ;; the box grows to the article, the scrolling box inside has
                ;; nothing left to scroll, and the toolbar ends up below the
                ;; bottom of the screen.
                :sx (sx {:height "100%" :minHeight 0
                         :display "flex" :flexDirection "column"
                         :backgroundColor "background.paper"})}
       (when article-loading? ($ mui/LinearProgress))
       ;; The page, and the only thing here that turns. Everything that
       ;; moves sideways is inside it and the bar below is not: `leg.vt`
       ;; finds it by this class and so does the view transition.
       ;;
       ;; The grain is on this box rather than on the column, because this
       ;; is the surface that slides: a page without it, moving across a
       ;; pane that has it, would be a sheet of different paper.
       ;;
       ;; `position: relative` for the two pages beside it: they are placed
       ;; against this box's edges and are out of its flow, so this is what
       ;; they are out of the flow *of*.
       ($ mui/Box {:className "leser-paper leser-vt-read"
                   :sx (sx {:flex "1" :minHeight 0 :position "relative"
                            :display "flex" :flexDirection "column"
                            :backgroundColor "background.paper"})}
          ;; The page before and the page after, a screen out on either
          ;; side. Only on a phone, and only once there is an article to be
          ;; beside.
          (when (and narrow? article)
            ($ :<>
               ($ Peek {:article before :feed (feed-of before) :side :previous})
               ($ Peek {:article after :feed (feed-of after) :side :next})))
          ;; The swipe is on the part that scrolls, which is the part a
          ;; finger is on while somebody reads. What it moves is the box
          ;; around it, peeks and all.
          ;;
          ;; A page is dragged out of the way of the next one, the way every
          ;; other thing with pages works: pulling left brings the next
          ;; article in from the right, pulling right puts this one back and
          ;; shows the one before it. The page follows the finger while it is
          ;; on the screen and the slide finishes what the finger started -
          ;; one movement, see `leg.vt`.
          ($ mui/Box {:sx (sx {:flex "1" :overflowY "auto"})
                      ;; This is the box the keys scroll, and finding it is
                      ;; also how leg.keys knows somebody is reading rather
                      ;; than looking at a list.
                      :data-keys "read"
                      ;; And how far down it is, written down as it moves, so
                      ;; the article can be opened here again tomorrow.
                      :onScroll (scroll/on-scroll id)
                      :onTouchStart swipe/on-start
                      :onTouchMove (swipe/on-move gesture)
                      :onTouchCancel (swipe/on-cancel gesture)
                      :onTouchEnd (swipe/on-end gesture)}
             (if-not article
               ($ mui/Box {:sx (sx {:padding "3rem 1rem" :textAlign "center"
                                    :color "text.secondary"})}
                  ($ mui/Typography "Pick something to read."))
               ($ Sheet {:article article :feed (feed-of article)}))))
       ;; Under the article and not over it: it is what a thumb reaches
       ;; without letting go of the phone, and everything on it - one along,
       ;; star, read - is pressed while reading rather than before.
       (when article
         ($ Toolbar {:article article :narrow? narrow?
                     :previous? (state/has-sibling? st -1)
                     :next? (state/has-sibling? st 1)
                     :position (state/position st)})))))
